import React, { useMemo } from "react";
import { GameState, PlayerState } from "../../core/protocol/types";
import MultiPlayerResultScreen from "./ResultScreen/MultiPlayerResultScreen";
import SinglePlayerResultScreen from "./ResultScreen/SinglePlayerResultScreen";
import "./ResultScreen.css";

interface ResultScreenProps {
  gameState: GameState | null;
  localStats?: { score: number; level: number; lines: number };
  isSinglePlayer?: boolean;
  onQuit: () => void;
  onRestart?: () => void;
  gameStats?: Record<string, { getValue: () => number }>; // For single player: game.stats object
  highScoreRank?: number; // For single player: high score rank (-1 if no high score)
}

const ResultScreen: React.FC<ResultScreenProps> = ({
  gameState,
  localStats,
  isSinglePlayer = false,
  onQuit,
  onRestart,
  gameStats,
  highScoreRank = -1,
}) => {
  // For single player, create a mock game state from local stats
  const displayState = useMemo(() => {
    if (isSinglePlayer && localStats) {
      // Create a mock player state for single player
      const mockPlayer: PlayerState = {
        connectionId: "local",
        name: "Player",
        numPlayer: 0,
        team: 0,
        score: localStats.score,
        frags: 0,
        deaths: 1, // Player died, so 1 death
        lines: localStats.lines,
        level: localStats.level,
        isActive: false,
        state: "dead",
        handicap: 0,
        isAttacking: false,
        board: {
          width: 10,
          height: 20,
          grid: Array(20).fill(null).map(() => Array(10).fill(0)),
          clearingLines: [],
          pendingGarbage: [],
        },
        ppm: 0,
        bpm: 0,
      };

      const mockGameState: GameState = {
        players: new Map([[0, mockPlayer]]),
        connections: new Map([["local", mockPlayer]]),
        gameSeed: 0,
        isPaused: false,
        gameMode: "normal",
        isServer: true,
        localConnectionId: "local",
        status: "finished",
      };

      return mockGameState;
    }
    return gameState;
  }, [gameState, localStats, isSinglePlayer]);

  if (!displayState || displayState.status !== "finished") {
    return null;
  }

  if (isSinglePlayer) {
    return (
      <SinglePlayerResultScreen
        gameState={displayState}
        localStats={localStats!}
        onQuit={onQuit}
        onRestart={onRestart}
        gameStats={gameStats}
        highScoreRank={highScoreRank}
      />
    );
  }

  return (
    <MultiPlayerResultScreen
      gameState={displayState}
      onQuit={onQuit}
      onRestart={onRestart}
    />
  );
};

export default ResultScreen;
